import React, { useEffect, useState } from "react";
import QRCode from "qrcode.react";
import { Check, Copy, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { fetchAuthCode, AuthCodeInfo } from "../utils/advancedSettings";

interface SecureQrCardProps {
  streamName: string;
  uid: string;
  color?: string;
}

/**
 * The QR code for streams that use "Require authentication" in the app. Attendees who open the plain
 * stream link only see a notice; this link (stream URL + ?auth=<code>) lets them in. Refreshing replaces the
 * code, so anyone holding the old QR code needs the new one.
 */
const SecureQrCard: React.FC<SecureQrCardProps> = ({ streamName, uid, color }) => {
  const [info, setInfo] = useState<AuthCodeInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async (refresh: boolean) => {
    setBusy(true);
    setError(null);
    try {
      setInfo(await fetchAuthCode(streamName, uid, refresh));
    } catch (err) {
      console.error("Secure QR code request failed:", err);
      setError("Could not get the secure QR code. Start the stream from the app once, then try again.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (streamName && uid) load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamName, uid]);

  const handleCopy = async () => {
    if (!info) return;
    try {
      await navigator.clipboard.writeText(info.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      window.prompt("Copy the secure link:", info.url);
    }
  };

  const handleRefresh = async () => {
    if (!window.confirm("Create a new secure QR code? Anyone who already has the old one will need to scan the new one.")) return;
    await load(true);
  };

  return (
    <div className="glass-card rounded-[2rem] p-8">
      <div className="flex flex-col items-center text-center space-y-5">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold uppercase tracking-wider">
          <ShieldCheck size={14} />
          Secure QR code
        </div>
        {error ? (
          <p className="text-sm text-red-600" role="alert">{error}</p>
        ) : !info ? (
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
        ) : (
          <>
            <div className="bg-white p-5 rounded-[2rem] shadow-lg">
              <QRCode value={info.url} size={180} fgColor={color || "#1e293b"} level="H" />
            </div>
            <p className="text-slate-600 text-sm">
              Share this with attendees of sessions where "Require authentication" is checked in the app. The regular QR code above shows them only a notice.
            </p>
          </>
        )}
        <div className="flex gap-2 w-full">
          <button
            onClick={handleCopy}
            disabled={!info}
            className="flex-1 py-3 rounded-2xl font-bold border bg-white border-slate-200 text-slate-700 hover:border-emerald-300 hover:text-emerald-700 flex items-center justify-center gap-2 disabled:opacity-50"
            aria-label="Copy secure link"
          >
            {copied ? <Check size={18} /> : <Copy size={18} />}
            {copied ? "Copied" : "Copy secure link"}
          </button>
          <button
            onClick={handleRefresh}
            disabled={busy}
            className="px-4 py-3 rounded-2xl font-bold border bg-white border-slate-200 text-slate-700 hover:border-amber-300 hover:text-amber-700 flex items-center justify-center disabled:opacity-50"
            aria-label="Create a new secure QR code"
            title="Create a new secure QR code"
          >
            <RefreshCw size={18} className={busy ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecureQrCard;
